// Racine de l'application : cache de requêtes, session, préférences, routes.
import { FournisseurLien, type ProprietesLien } from '@cc/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Link, Navigate, RouterProvider } from 'react-router';
import { ErreurApi } from './api/erreurs';
import { FournisseurMessages } from './commun/Messages';
import { CoquilleAdministration } from './coquille/CoquilleAdministration';
import { GardeContexte, GardeSession } from './Gardes';
import { PageActivationMfa } from './pages/acces/PageActivationMfa';
import { PageChoixContexte } from './pages/acces/PageChoixContexte';
import { PageConnexion } from './pages/acces/PageConnexion';
import { PageIntrouvable } from './pages/PageAttente';
import { PagePreferences } from './pages/PagePreferences';
import { routesAdministration } from './pages/administration/routes';
import { routesDemarches } from './pages/demarches/routes';
import { routesInterventions } from './pages/interventions/routes';
import { routesMarches } from './pages/marches/routes';
import { routesCitoyen } from './pages/citoyen/routes';
import { routesMetier } from './pages/metier/routes';
import { routesPlateforme } from './pages/plateforme/routes';
import { routesRecettes } from './pages/recettes/routes';
import { routesProprete } from './pages/proprete/routes';
import { routesInspections } from './pages/inspections/routes';
import { routesBudget } from './pages/budget/routes';
import { routesAchats } from './pages/achats/routes';
import { routesTravaux } from './pages/travaux/routes';
import { routesPatrimoine } from './pages/patrimoine/routes';
import { routesConseils } from './pages/conseils/routes';
import { routesPersonnel } from './pages/personnel/routes';
import { routesProximite } from './pages/proximite/routes';
import { routesEvenements } from './pages/evenements/routes';
import { routesUrbanisme } from './pages/urbanisme/routes';
import { routesReclamations } from './pages/reclamations/routes';
import { routesRendezVous } from './pages/rendez-vous/routes';
import { routesTableauDeBord } from './pages/tableau/routes';
import { routesTerrain } from './pages/terrain/routes';
import { routesSupervision } from './pages/supervision/routes';
import { routesSocial } from './pages/social/routes'; // Lot L7 : actions sociales (M20).
import { routesAssistance } from './pages/assistance/routes'; // Lot L7 : lecture assistée.
import { FournisseurPreferences } from './preferences/FournisseurPreferences';
import { FournisseurSession } from './session/FournisseurSession';

// Vitrine des composants : chargée uniquement en développement, absente de la construction.
const PageVitrine = import.meta.env.DEV ? lazy(() => import('./vitrine/PageVitrine')) : null;

export function creerClientRequetes(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Pas de nouvel essai sur une erreur fonctionnelle (4xx) ; deux sur une coupure réseau.
        retry: (n, e) => (e instanceof ErreurApi && e.statut >= 400 && e.statut < 500 ? false : n < 2),
        refetchOnWindowFocus: false,
        staleTime: 15_000,
        networkMode: 'online',
      },
      mutations: { retry: false },
    },
  });
}

function LienRouteur({ href, ...reste }: ProprietesLien) {
  return <Link to={href} {...reste} />;
}

const routeur = createBrowserRouter([
  { path: '/connexion', element: <PageConnexion /> },
  ...routesCitoyen,
  ...routesTerrain,
  ...(PageVitrine
    ? [
        {
          path: '/vitrine',
          element: (
            <Suspense fallback={null}>
              <PageVitrine />
            </Suspense>
          ),
        },
      ]
    : []),
  {
    element: <GardeSession />,
    children: [
      { path: '/contexte', element: <PageChoixContexte /> },
      { path: '/mfa/activer', element: <PageActivationMfa /> },
      {
        element: <GardeContexte />,
        children: [
          {
            element: <CoquilleAdministration />,
            children: [
              ...routesTableauDeBord,
              ...routesMetier,
              ...routesDemarches,
              ...routesMarches,
              ...routesInterventions,
              ...routesRendezVous,
              ...routesReclamations,
              ...routesRecettes,
              ...routesProprete,
              ...routesInspections,
              ...routesBudget,
              ...routesAchats, ...routesTravaux,
              ...routesPatrimoine, // Lot L4-A : patrimoine (M10) et équipements publics (M19).
              ...routesConseils, // Lot L5-B : conseils municipaux et départementaux (M16).
              ...routesPersonnel, // Lot L5-A : personnel sans paie (M11).
              ...routesProximite, ...routesEvenements, // Lot L5-C1 : quartiers et participation (M17), événements (M21).
              ...routesUrbanisme, // Lot L5-C2 : urbanisme et aménagement (M18).
              ...routesSupervision, // Lot L6-A : supervision nationale (M24), à la place de l'attente /supervision/*.
              ...routesSocial, // Lot L7 : actions sociales (M20).
              ...routesAssistance, // Lot L7 : lecture assistée des documents.
              ...routesAdministration,
              ...routesPlateforme,
              { path: '/preferences', element: <PagePreferences /> },
              { path: '/administration', element: <Navigate to="/administration/utilisateurs" replace /> },
              { path: '*', element: <PageIntrouvable /> },
            ],
          },
        ],
      },
    ],
  },
]);

const client = creerClientRequetes();

export function Application() {
  return (
    <QueryClientProvider client={client}>
      <FournisseurLien composant={LienRouteur}>
        <FournisseurMessages>
          <FournisseurSession>
            <FournisseurPreferences>
              <RouterProvider router={routeur} />
            </FournisseurPreferences>
          </FournisseurSession>
        </FournisseurMessages>
      </FournisseurLien>
    </QueryClientProvider>
  );
}
